<template>
  <LoadingOverlay :is-active="bookingApiStatus === 'LOADING'" />
  <div v-if="pageContent && bookingApiStatus === 'SUCCESS'">
    <HeroBannerGeneral
      v-if="heroBanner"
      :responsive-background-image="heroBanner.responsiveBackgroundImage"
    />
    <section class="cancel-booking">
      <Article v-bind="cmsBlockToProps(pageContent.header)" />
      <div class="cancel-booking__details">
        <p class="cancel-booking__title">Booking Details</p>
        <table class="cancel-booking__table">
          <tr>
            <td class="cancel-booking__text">Booking reference</td>
            <td ref="booking-reference" class="cancel-booking__content">
              {{ booking.displayJobNumber }}
            </td>
          </tr>
          <tr>
            <td class="cancel-booking__text">Address</td>
            <td ref="address" class="cancel-booking__content">
              {{ booking.fullAddress }}
            </td>
          </tr>
          <tr>
            <td class="cancel-booking__text">Date</td>
            <td ref="date" class="cancel-booking__content">
              {{ booking.appointmentDateFormatted }}
            </td>
          </tr>
          <tr>
            <td class="cancel-booking__text">Time</td>
            <td ref="time" class="cancel-booking__content">
              {{ booking.appointmentTimeSlot }}
            </td>
          </tr>
        </table>
      </div>
      <div class="cancel-booking__nav-buttons">
        <NavButton v-bind="cmsBlockToProps(pageContent.navButton)" />
        <AppButton
          v-bind="cmsBlockToProps(pageContent.editBookingButton)"
          :to="{
            name: 'reschedule-appointment',
            query: { jobNumber: jobNumber },
          }"
        />
      </div>
      <hr class="cancel-booking__horizontal-rule" />
      <div v-if="!cancellationReasonsError">
        <Article v-bind="cmsBlockToProps(pageContent.cancelAppointmentText)" />
        <p class="cancel-booking__reason-text">Reason for cancellation</p>
        <Combobox
          v-model="selectedCancellationReason"
          :options="cancellationReasons"
          label="Select a reason"
          input-id="cancellation-reason"
          class="cancel-booking__combobox"
        />
        <div v-if="showRescheduleOption">
          <p class="cancel-booking__reschedule-text">
            If you're not going to be home,
            <router-link
              :to="{
                name: 'reschedule-appointment',
                query: { jobNumber: jobNumber },
              }"
            >
              you can reschedule your booking instead of cancelling</router-link
            >.
          </p>
        </div>
        <div class="cancel-booking__buttons">
          <AppButton
            v-if="showRescheduleOption"
            v-bind="cmsBlockToProps(pageContent.rescheduleButton)"
            :to="{
              name: 'reschedule-appointment',
              query: { jobNumber: jobNumber },
            }"
          />
          <AppButton
            v-bind="cmsBlockToProps(pageContent.cancelButton)"
            ref="submit-button"
            :disabled="!selectedCancellationReason || status.error"
            data-dd-action-name="Cancel Booking Button"
            :loading="status.loading"
            @click="cancelBooking"
          />
        </div>
      </div>
      <FeedbackDialog
        v-if="status.error || cancellationReasonsError"
        class="cancel-booking__error-message"
        variant="negative"
        :message="pageContent.errorMessage"
      />
    </section>
  </div>
</template>

<script>
import HeroBannerGeneral from "@soenergy/frontend-library/src/components/HeroBannerGeneral.vue"
import cmsPreviewMixin from "@soenergy/frontend-library/src/mixins/cmsPreviewMixin"
import cancelBooking from "soenergy-cms-loader!?path=smbp/cancel-booking"
import Article from "@soenergy/frontend-library/src/components/Article.vue"
import NavButton from "@soenergy/frontend-library/src/components/NavButton.vue"
import AppButton from "@soenergy/frontend-library/src/components/AppButton.vue"
import Combobox from "@soenergy/frontend-library/src/components/Combobox.vue"
import GatewayPortal from "../services/GatewayPortal"
import ErrorReporter from "@soenergy/frontend-library/src/services/ErrorReporter"
import { mapState, mapGetters } from "vuex"
import FeedbackDialog from "@soenergy/frontend-library/src/components/FeedbackDialog.vue"
import LoadingOverlay from "@soenergy/frontend-library/src/components/LoadingOverlay.vue"
import { CANCELLATION_REASON_LABELS, RESCHEDULE_REASONS } from "../constants"

export default {
  components: {
    HeroBannerGeneral,
    Article,
    NavButton,
    AppButton,
    Combobox,
    FeedbackDialog,
    LoadingOverlay,
  },
  mixins: [cmsPreviewMixin({ story: cancelBooking })],
  data() {
    return {
      cancellationReasons: [],
      selectedCancellationReason: null,
      cancellationReasonsError: false,
      status: {
        loading: false,
        error: false,
      },
    }
  },
  computed: {
    ...mapState({
      customerId: (state) => state.customerId,
      bookingApiStatus: (state) => state.bookingDetails.bookingApiStatus,
      bookings: (state) => state.bookingDetails.bookings,
    }),
    ...mapGetters({ mopCode: "mopCode" }),
    jobNumber() {
      return this.$route.query.jobNumber
    },
    booking() {
      return this.bookings.find(
        (booking) => booking.mopJobNumber == this.jobNumber
      )
    },
    heroBanner() {
      return this.pageContent.heroBanner[0]
    },
    showRescheduleOption() {
      return (
        this.selectedCancellationReason &&
        RESCHEDULE_REASONS.includes(this.selectedCancellationReason.reason)
      )
    },
  },
  mounted() {
    this.$store.dispatch("bookingDetails/fetchBookingData")
    this.fetchCancellationReasons()
  },
  methods: {
    fetchCancellationReasons() {
      GatewayPortal.getCancellationReasons()
        .then((response) => {
          this.cancellationReasons = response.data
            .filter((item) => CANCELLATION_REASON_LABELS[item.reason])
            .map((item) => ({
              reason: item.reason,
              label: CANCELLATION_REASON_LABELS[item.reason],
            }))
        })
        .catch((err) => {
          this.cancellationReasonsError = true
          ErrorReporter.report(err)
        })
    },
    cancelBooking() {
      this.status.error = false
      this.status.loading = true

      GatewayPortal.cancelBooking(
        this.customerId,
        this.jobNumber,
        this.selectedCancellationReason.reason,
        this.mopCode
      )
        .then(() => this.$router.push({ path: "/booking-cancelled" }))
        .catch((err) => {
          this.status.error = true
          this.status.loading = false
          throw err
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.cancel-booking {
  margin: $space-8 $space-4;

  @include md {
    margin: $space-8 auto;
    width: $width-6;
  }

  &__details {
    margin: $space-8 0;
  }

  &__title {
    font-size: $font-size-4;
    font-weight: $weight-medium;
    margin-bottom: $space-5;

    @include lg {
      font-size: $font-size-5;
    }
  }

  &__table {
    width: 100%;
    border-spacing: 0 $space-2;
    border-collapse: unset;
  }

  &__text {
    font-size: $font-size-3;
    font-weight: $weight-medium;
    padding-right: $space-3;

    @include lg {
      font-size: $font-size-4;
    }
  }

  &__content {
    font-size: $font-size-3;
    padding-left: $space-4;

    @include lg {
      font-size: $font-size-4;
    }
  }

  &__nav-buttons {
    display: flex;
    gap: $space-4;

    @include md {
      gap: $space-5;
    }
  }

  &__horizontal-rule {
    border: solid 1px $grey-500;
    margin-top: $space-8;

    @include md {
      margin-top: $space-9;
    }
  }

  &__reason-text {
    font-size: $font-size-3;
    font-weight: $weight-medium;
    margin-top: $space-5;

    @include lg {
      margin-top: $space-7;
    }
  }

  &__combobox {
    margin-bottom: $space-6;

    @include lg {
      margin-bottom: $space-8;
    }
  }

  &__reschedule-text {
    font-size: $font-size-3;

    @include lg {
      font-size: $font-size-4;
    }
  }

  &__buttons {
    display: flex;
    gap: $space-4;
    margin-top: $space-8;

    @include lg {
      margin-top: $space-10;
    }
  }

  &__error-message {
    margin: $space-8 0;
  }
}
</style>
